import { useEffect, useState } from 'react';
import { PiCheck, PiSpinner } from 'react-icons/pi';
import Button from '../../atoms/buttons/button';
import { Text } from '../../atoms/text';
import { useI18n } from '../../../hooks/provider-bounded/i18n';
import type { InlineSaveFeedbackProps } from '../../../types/inline-save-feedback.types';

/** Small, shared autosave feedback beside a control, with persistent failure recovery. */
export function InlineSaveFeedback({
  saving,
  saved,
  error,
  onRetry,
}: InlineSaveFeedbackProps) {
  const { t } = useI18n();
  const [showSaved, setShowSaved] = useState(saved);
  useEffect(() => {
    setShowSaved(saved);
    if (!saved) return;
    const timer = window.setTimeout(() => setShowSaved(false), 3000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  if (error && !saving) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2">
        <Text size="tiny" color="danger">
          {error}
        </Text>
        {onRetry && (
          <Button
            size="sm"
            appearance="ghost"
            variant="neutral"
            onClick={onRetry}
          >
            {t('common.saveFeedback.retry')}
          </Button>
        )}
      </div>
    );
  }
  if (!saving && (!saved || !showSaved)) return null;
  return (
    <div role="status" aria-atomic="true" className="flex items-center gap-1.5">
      {saving ? (
        <PiSpinner className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <PiCheck className="h-3.5 w-3.5" aria-hidden />
      )}
      <Text size="tiny" color="secondary">
        {t(saving ? 'common.saveFeedback.saving' : 'common.saveFeedback.saved')}
      </Text>
    </div>
  );
}
